/**
 * pauseBalanceAdapter — `GET /api/v1/subscription/pause/balance` → les libellés du parcours de
 * pause, et le corps de `POST /api/v1/subscription/pause`.
 *
 * Port littéral de `ParentPauseSubscriptionModal.js` : les quatre motifs (`:29-34`), les replis
 * des règles (`:147-156`), le récapitulatif (`:288-336`) et le corps de la soumission (`:242-254`).
 * Le design system ne reçoit que des phrases : les chiffres métier meurent ici. Le solde ne s'écrit
 * plus nulle part — ni le bloc de tête du legacy (`:414-436`), ni « Il lui restera … » du
 * récapitulatif (Wajih, 30/09) : seul le restant sert encore, aux bornes et aux refus
 * (`pauseDateRules`) ; le quota annuel et les jours consommés ne sont plus lus.
 *
 * ⚠️ Les replis ne sont PAS une grille de secours choisie ici : ils suivent
 * `SubscriptionPauseService` côté backend — les limites qu'une famille pose seule
 * (`SELF_SERVICE_*` : 16 jours, 1 mois depuis la résiliation) et 5 / 28. Ils ne servent que quand `getParentPauseBalance` n'a pas (encore) répondu.
 */
import moment from "moment"

import { formatDayMonthYear } from "../../../ui/formatters/dateFormatter"
import { type UIPauseReason, type UIPauseRecap } from "../../../ui/types"

/** Ce que l'adaptateur lit du solde servi par `ParentSubscriptionPauseController::balance`. */
export type ApiParentPauseBalance = {
    remaining_days?: number
    /** Ce qu'une famille pose seule depuis l'app — pas la limite du BO. */
    max_consecutive_days?: number
    /** Ce qu'elle pose quand elle arrive du parcours de résiliation. */
    from_cancellation_max_days?: number
    /** `YYYY-MM-DD` — le premier début possible après sa dernière pause, `null` sans attente. */
    next_pause_min_start?: string | null
    min_duration_days?: number
    keep_lives_max_days?: number
}

/** Les quatre motifs de `REASON_OPTIONS`, dans leur ordre. */
export const PAUSE_REASON_OPTIONS: UIPauseReason[] = [
    { value: "vacances", label: "Vacances" },
    { value: "maladie", label: "Maladie" },
    { value: "stage", label: "Stage" },
    { value: "autre", label: "Autre" },
]

/** Les replis du legacy quand l'appel de solde échoue (`ParentPauseSubscriptionModal.js:149-156`). */
export const PAUSE_RULES_FALLBACK = {
    maxConsecutiveDays: 16,
    fromCancellationMaxDays: 31,
    minDurationDays: 5,
    keepLivesMaxDays: 28,
} as const

export type PauseRules = {
    /** `null` tant que le serveur n'a pas répondu : le legacy ne le remplace par rien. */
    remainingDays: number | null
    /** La limite de CE parcours : celle du parcours de résiliation quand le parent en arrive. */
    maxConsecutiveDays: number
    /** `YYYY-MM-DD`, ou `null` quand rien ne retient la famille. */
    nextPauseMinStart: string | null
    minDurationDays: number
    keepLivesMaxDays: number
}

export function toPauseRules(api: ApiParentPauseBalance | null | undefined, fromCancellation = false): PauseRules {
    return {
        remainingDays: api?.remaining_days ?? null,
        maxConsecutiveDays: fromCancellation
            ? (api?.from_cancellation_max_days ?? PAUSE_RULES_FALLBACK.fromCancellationMaxDays)
            : (api?.max_consecutive_days ?? PAUSE_RULES_FALLBACK.maxConsecutiveDays),
        nextPauseMinStart: api?.next_pause_min_start ?? null,
        minDurationDays: api?.min_duration_days ?? PAUSE_RULES_FALLBACK.minDurationDays,
        keepLivesMaxDays: api?.keep_lives_max_days ?? PAUSE_RULES_FALLBACK.keepLivesMaxDays,
    }
}

/** « 1 jour » / « 21 jours » — le `dayLabel` du legacy (`:268`). */
export function pauseDayLabel(days: number): string {
    return `${days} jour${days > 1 ? "s" : ""}`
}

/** Le nombre de jours consommés par la fenêtre posée (`:158-161`). */
export function pauseDurationDays(startDate: string | null, resumeDate: string | null): number {
    if (!startDate || !resumeDate) return 0
    return Math.max(0, moment(resumeDate).diff(moment(startDate), "days"))
}

/**
 * Ce que la pause décale de l'engagement — `null` sans engagement en cours : il n'y a rien à
 * décaler, et le récapitulatif n'a pas de carte d'engagement.
 *
 * Les deux dates d'engagement s'écrivent « 15 novembre 2026 » et non « 15/11/2026 » comme le
 * legacy : elles voisinent dans la même carte les dates « Du » / « Reprise le », que le design
 * system met en forme ainsi.
 */
export function toUIPauseRecap(args: {
    durationDays: number
    /** `user.commitment_ends_at`, tel que le backend le sert. */
    commitmentEndsAt?: string | null
    /** Maintenant — injecté pour que la règle « engagement encore en cours » se teste. */
    now?: moment.Moment
}): UIPauseRecap | null {
    const { durationDays, commitmentEndsAt, now } = args
    if (!commitmentEndsAt || !moment(commitmentEndsAt).isAfter(now ?? moment())) return null

    return {
        commitmentEndLabel: formatDayMonthYear(commitmentEndsAt),
        commitmentShiftedLabel: formatDayMonthYear(
            moment(commitmentEndsAt).add(durationDays, "days").format("YYYY-MM-DD")
        ),
    }
}

export type ApiPauseRequestBody = {
    start_date: string
    resume_date: string
    reason: string
    tutor_loss_acknowledged: boolean
    /** Le serveur n'accorde le mois du parcours de résiliation que sur ce signal. */
    from_cancellation: boolean
}

/** Le corps de `pauseSubscriptionParent`, celui de `handleSubmit` (`:242-254`) et sa provenance. */
export function pauseRequestBody(args: {
    startDate: string
    resumeDate: string
    reason: string
    tutorLossAcknowledged: boolean
    fromCancellation: boolean
}): ApiPauseRequestBody {
    return {
        start_date: moment(args.startDate).format("YYYY-MM-DD"),
        resume_date: moment(args.resumeDate).format("YYYY-MM-DD"),
        reason: args.reason,
        tutor_loss_acknowledged: args.tutorLossAcknowledged,
        from_cancellation: args.fromCancellation,
    }
}
